import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { CssBaseline, Typography } from '@mui/material';
import Header from './Header';
import StyledComponents from './styles/StyledComponents';
import { motion, AnimatePresence } from 'framer-motion';
import { animations } from './styles/theme';
import LoadingFallback from './components/LoadingFallback';
import PerformanceWrapper from './components/PerformanceWrapper';
import { PriceProvider } from './pages/PriceContextOptimized';
import { SelectionProvider } from './customer/SelectionContextOptimized';
import { CustomerProvider } from './pages/CustomerContext';
import { VendorProvider } from './vendor/VendorContext';
import CustomerLayout from './customer/CustomerLayout';
import ActingAsIndicator from './components/ActingAsIndicator';
import api from './api';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import { textureAnimations } from './styles/luxuryTextures';

// Lazy load all route components
// const CustomerLanding = lazy(() => import('./pages/CustomerLanding'));
const StaffPortalLanding = lazy(() => import('./pages/StaffPortalLanding'));
// const CustomerLogin = lazy(() => import('./components/CustomerLogin'));
// const BusinessLogin = lazy(() => import('./components/BusinessLogin'));
const Login = lazy(() => import('./pages/Login'));
const VendorRegistration = lazy(() => import('./pages/VendorRegistration'));
const Register = lazy(() => import('./pages/Register'));
const MortgageDetails = lazy(() => import('./customer/MortgageDetails'));
const HouseSelection = lazy(() => import('./pages/HouseSelection'));
// New pricing-matrix selection pages
const LocationElevation = lazy(() => import('./pages/LocationElevation'));
const LocationLotType = lazy(() => import('./pages/LocationLotType'));
const LocationPatioDeck = lazy(() => import('./pages/LocationPatioDeck'));
const LocationBasement = lazy(() => import('./pages/LocationBasement'));
const LocationGarage = lazy(() => import('./pages/LocationGarage'));
const LocationCeilings = lazy(() => import('./pages/LocationCeilings'));
const LocationWindows = lazy(() => import('./pages/LocationWindows'));
const LocationFlooring = lazy(() => import('./pages/LocationFlooring'));
const LocationCabinets = lazy(() => import('./pages/LocationCabinets'));
const LocationSiding = lazy(() => import('./pages/LocationSiding'));
const LocationRoofSoffit = lazy(() => import('./pages/LocationRoofSoffit'));
const LocationPorch = lazy(() => import('./pages/LocationPorch'));
const LocationStone = lazy(() => import('./pages/LocationStone'));
const LocationFrontDoor = lazy(() => import('./pages/LocationFrontDoor'));
const LocationKitchenBacksplash = lazy(() => import('./pages/LocationKitchenBacksplash'));
const LocationKitchenFaucet = lazy(() => import('./pages/LocationKitchenFaucet'));
const LocationCountertops = lazy(() => import('./pages/LocationCountertops'));
const LocationSinks = lazy(() => import('./pages/LocationSinks'));
const LocationAppliances = lazy(() => import('./pages/LocationAppliances'));
const LocationBathroomFixtures = lazy(() => import('./pages/LocationBathroomFixtures'));
const LocationShower = lazy(() => import('./pages/LocationShower'));
const LocationToilets = lazy(() => import('./pages/LocationToilets'));
const LocationLighting = lazy(() => import('./pages/LocationLighting'));
const LocationFireplace = lazy(() => import('./pages/LocationFireplace'));
const LocationInterior = lazy(() => import('./pages/LocationInterior'));
const LocationDoorHandles = lazy(() => import('./pages/LocationDoorHandles'));
const LocationGarageDoor = lazy(() => import('./pages/LocationGarageDoor'));
const LocationMechanical = lazy(() => import('./pages/LocationMechanical'));
const SelectionSummary = lazy(() => import('./pages/SelectionSummary'));
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'));
const SalesReps = lazy(() => import('./pages/SalesReps'));
const Customers = lazy(() => import('./pages/Customers'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const IronBluffMapper = lazy(() => import('./pages/admin/IronBluffMapper'));
const ProjectManagerDashboard = lazy(() => import('./pages/ProjectManagerDashboard'));
const SuperintendentDashboard = lazy(() => import('./pages/SuperintendentDashboard'));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard'));
// New Simple Vendor Components
const VendorDashboard = lazy(() => import('./vendor/pages/VendorDashboard'));
const VendorBidGrid = lazy(() => import('./vendor/pages/VendorBidGrid'));
const VendorBidGridEnhanced = lazy(() => import('./vendor/pages/VendorBidGridEnhanced'));
const VendorGridIntegration = lazy(() => import('./vendor/components/VendorGridIntegration'));
const VendorBidComparison = lazy(() => import('./vendor/pages/VendorBidComparison'));

const { AnimatedContainer } = StyledComponents;

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState(localStorage.getItem('role') || null);
  const [actingAsCustomer, setActingAsCustomer] = useState(null);
  const hasCheckedAuth = useRef(false);
  
  // Debug logging
  console.log('App.js rendering, current path:', location.pathname, 'role:', role);
  
  // Intercept navigation for debugging
  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      console.log('🔀 PUSHSTATE Navigation:', args[2]);
      return originalPushState.apply(window.history, args);
    };
    
    window.history.replaceState = function(...args) {
      console.log('🔁 REPLACESTATE Navigation:', args[2]);
      return originalReplaceState.apply(window.history, args);
    };
    
    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  useEffect(() => {
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedRole = localStorage.getItem('role');
      
      if (!token) {
        setRole(null);
        localStorage.removeItem('role');
        return;
      }

      // If we have both token and role, try to use them first
      if (storedRole) {
        setRole(storedRole);
      }

      try {
        const res = await api.get('/auth/profile');
        if (res.status === 200) {
          // Only update if role actually changed
          if (storedRole !== res.data.role) {
            localStorage.setItem('role', res.data.role);
            setRole(res.data.role);
          }
          
          // For customers, ensure we have their customer ID
          if (res.data.role === 'Customer' && res.data.customerId) {
            console.log('Customer authenticated with ID:', res.data.customerId);
            localStorage.setItem('customerId', res.data.customerId);
          }
        }
      } catch (err) {
        if (err.response?.status === 401) {
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const refreshResponse = await api.post('/auth/refresh', { refresh_token: refreshToken });
              localStorage.setItem('access_token', refreshResponse.data.access_token);
              if (refreshResponse.data.refresh_token) {
                localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
              }
              const retryRes = await api.get('/auth/profile');
              if (retryRes.status === 200) {
                localStorage.setItem('role', retryRes.data.role);
                setRole(retryRes.data.role);
              }
            } else {
              console.log('🚨 App.js: No refresh token - checking if acting as customer');
              console.trace('No refresh token clear stack trace');
              
              // Check if sales rep is acting as customer
              const currentRole = localStorage.getItem('role');
              const isActingAs = localStorage.getItem('actingAsCustomer');
              
              if (currentRole === 'Sales' && isActingAs) {
                console.log('Sales rep acting as customer - maintaining session');
                // Don't clear everything, just remove customer-specific data
                localStorage.removeItem('customerId');
              } else {
                localStorage.clear();
                setRole(null);
              }
            }
          } catch (refreshErr) {
            console.error('Refresh failed:', refreshErr);
            console.log('🚨 App.js: Refresh failed - checking if acting as customer');
            console.trace('Refresh failed clear stack trace');
            
            // Check if sales rep is acting as customer
            const currentRole = localStorage.getItem('role');
            const isActingAs = localStorage.getItem('actingAsCustomer');
            
            if (currentRole === 'Sales' && isActingAs) {
              console.log('Sales rep acting as customer - maintaining session during refresh failure');
            } else {
              localStorage.clear();
              setRole(null);
            }
          }
        } else {
          console.error('Auth check failed:', err);
          console.log('🚨 App.js: Auth check failed - checking if acting as customer');
          console.trace('Auth check failed clear stack trace');
          
          // Check if sales rep is acting as customer
          const currentRole = localStorage.getItem('role');
          const isActingAs = localStorage.getItem('actingAsCustomer');
          
          if (currentRole === 'Sales' && isActingAs) {
            console.log('Sales rep acting as customer - maintaining session during auth check failure');
          } else {
            localStorage.clear();
            setRole(null);
          }
        }
      }
    };

    checkAuth();
    
    // Restore acting as customer state
    const savedActingAs = localStorage.getItem('actingAsCustomer');
    const currentRole = localStorage.getItem('role');
    if (savedActingAs && currentRole === 'Sales') {
      try {
        setActingAsCustomer(JSON.parse(savedActingAs));
        console.log('Restored acting as customer state:', JSON.parse(savedActingAs));
      } catch (e) {
        console.error('Failed to parse actingAsCustomer:', e);
      }
    }
  }, []); // Remove navigate dependency to prevent loops

  // Navigation effect - only run when role changes and we're on login page
  useEffect(() => {
    if (role && (location.pathname === '/login' || location.pathname === '/business/login' || location.pathname === '/staff/login' || location.pathname === '/vendors/login')) {
      // Use setTimeout to prevent immediate re-render
      setTimeout(() => {
        switch (role) {
          case 'SuperAdmin': 
            navigate('/staff/admin-users', { replace: true }); 
            break;
          case 'Admin': 
            navigate('/staff/admin-dashboard', { replace: true }); 
            break;
          case 'Sales': 
            navigate('/staff/sales-dashboard', { replace: true }); 
            break;
          case 'Operations':
            navigate('/staff/operations-dashboard', { replace: true });
            break;
          case 'ProjectManager':
            navigate('/staff/project-manager-dashboard', { replace: true });
            break;
          case 'Superintendent':
            navigate('/staff/superintendent-dashboard', { replace: true });
            break;
          case 'Vendor':
            navigate('/vendors/dashboard', { replace: true });
            break;
          case 'Customer': 
            navigate('/customer/selection', { replace: true }); 
            break;
          default: 
            navigate('/', { replace: true });
        }
      }, 100);
    }
  }, [role, location.pathname]); // Remove navigate dependency

  const ProtectedRoute = ({ element, allowedRoles }) => {
    // Don't cause re-renders by calling localStorage in render
    const storedRole = role;
    const customerId = localStorage.getItem('customerId');
    const currentPath = location.pathname;

    // Debug logging for customer and vendor routes
    if (currentPath.startsWith('/vendor') || currentPath.startsWith('/customer')) {
      console.log('🔍 DEBUG: ProtectedRoute:', {
        currentPath,
        storedRole,
        allowedRoles,
        hasToken: !!localStorage.getItem('access_token'),
        customerId,
        vendorId: localStorage.getItem('vendorId'),
        isVendor: localStorage.getItem('isVendor')
      });
    }

    // If no role, redirect to appropriate login page
    if (!storedRole) {
      console.log('❌ No role found, redirecting to login');
      if (currentPath.startsWith('/staff') || currentPath.startsWith('/vendors')) {
        return <Navigate to="/business/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }

    // Check if role is allowed
    if (!allowedRoles.includes(storedRole)) {
      console.log('❌ Role not allowed:', storedRole, 'not in', allowedRoles);
      if (currentPath.startsWith('/staff') || currentPath.startsWith('/vendors')) {
        return <Navigate to="/business/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }

    const isSelectionPage =
      currentPath.startsWith('/customer/selection') ||
      currentPath.startsWith('/customer/location') ||
      currentPath === '/customer';

    // Sales role needs customer ID for selection pages
    if (storedRole === 'Sales' && isSelectionPage && !customerId) {
      console.log('⚠️ Sales user without customer ID on selection page - redirecting to sales dashboard');
      return <Navigate to="/staff/sales-dashboard" replace />;
    }
    
    // Debug customer role on selection pages
    if (storedRole === 'Customer' && isSelectionPage) {
      console.log('🚶 Customer on selection page:', { customerId, currentPath });
    }

    return element;
  };

  const CustomerRoutes = () => (
    <CustomerLayout>
      <SelectionProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </SelectionProvider>
    </CustomerLayout>
  );

  // Determine if we should show the header
  const showHeader = !['/', '/staff'].includes(location.pathname) && 
                    !location.pathname.endsWith('/landing');

  return (
    <>
      <CssBaseline />
      {showHeader && <Header role={role} />}
      {role === 'Sales' && <ActingAsIndicator />}
      <AnimatePresence mode="wait">
        {role === null && !['/', '/staff'].includes(location.pathname) && !location.pathname.includes('/login') && (
          <motion.div
            {...animations.fadeIn}
            exit={{ opacity: 0 }}
            style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}
          >
            <Typography variant="h4">Loading...</Typography>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatedContainer
        {...animations.fadeIn}
        style={{ 
          maxWidth: ['/', '/staff'].includes(location.pathname) ? '100%' : '1200px', 
          margin: '0 auto', 
          padding: showHeader ? '5rem 1rem 2rem' : '0' 
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Public Landing Pages */}
          <Route path="/" element={<Login setRole={setRole} />} />
          <Route path="/staff" element={<StaffPortalLanding />} />
          
          {/* Auth Routes */}
          {/* <Route path="/login" element={<CustomerLogin setRole={setRole} />} /> */}
          {/* <Route path="/business/login" element={<BusinessLogin setRole={setRole} />} /> */}
          <Route path="/login" element={<Login setRole={setRole} />} />
          <Route path="/business/login" element={<Login setRole={setRole} />} />
          <Route path="/vendors/register" element={<VendorRegistration />} />
          <Route path="/register" element={<Register />} />
          
          {/* Protected Customer Routes */}
          <Route path="/customer" element={<ProtectedRoute element={<CustomerRoutes />} allowedRoles={['Customer', 'Sales', 'Admin', 'SuperAdmin']} />}>
            <Route index element={<Navigate to="/customer/selection" replace />} />
            <Route path="selection" element={<PerformanceWrapper id="HouseSelection"><HouseSelection /></PerformanceWrapper>} />
            <Route path="mortgage" element={<MortgageDetails />} />
            {/* Structure */}
            <Route path="location/elevation" element={<LocationElevation />} />
            <Route path="location/lot-type" element={<LocationLotType />} />
            <Route path="location/patio-deck" element={<LocationPatioDeck />} />
            <Route path="location/basement" element={<LocationBasement />} />
            <Route path="location/garage" element={<LocationGarage />} />
            {/* Interior */}
            <Route path="location/ceilings" element={<LocationCeilings />} />
            <Route path="location/windows" element={<LocationWindows />} />
            <Route path="location/flooring" element={<LocationFlooring />} />
            <Route path="location/cabinets" element={<LocationCabinets />} />
            {/* Exterior */}
            <Route path="location/siding" element={<LocationSiding />} />
            <Route path="location/roof-soffit" element={<LocationRoofSoffit />} />
            <Route path="location/porch" element={<LocationPorch />} />
            <Route path="location/stone" element={<LocationStone />} />
            <Route path="location/front-door" element={<LocationFrontDoor />} />
            {/* Kitchen */}
            <Route path="location/kitchen-backsplash" element={<LocationKitchenBacksplash />} />
            <Route path="location/kitchen-faucet" element={<LocationKitchenFaucet />} />
            <Route path="location/countertops" element={<LocationCountertops />} />
            <Route path="location/sinks" element={<LocationSinks />} />
            <Route path="location/appliances" element={<LocationAppliances />} />
            {/* Bathrooms */}
            <Route path="location/bathroom-fixtures" element={<LocationBathroomFixtures />} />
            <Route path="location/shower" element={<LocationShower />} />
            <Route path="location/toilets" element={<LocationToilets />} />
            {/* Features */}
            <Route path="location/lighting" element={<LocationLighting />} />
            <Route path="location/fireplace" element={<LocationFireplace />} />
            <Route path="location/interior" element={<LocationInterior />} />
            <Route path="location/door-handles" element={<LocationDoorHandles />} />
            <Route path="location/garage-door" element={<LocationGarageDoor />} />
            <Route path="location/mechanical" element={<LocationMechanical />} />
            <Route path="selection-summary" element={<PerformanceWrapper id="SelectionSummary"><SelectionSummary /></PerformanceWrapper>} />
          </Route>

          {/* Staff Portal Routes */}
          <Route path="/staff/sales-dashboard" element={<ProtectedRoute element={<SalesDashboard />} allowedRoles={['Sales']} />} />
          <Route path="/staff/sales-reps" element={<ProtectedRoute element={<SalesReps />} allowedRoles={['Sales']} />} />
          <Route path="/staff/customers" element={<ProtectedRoute element={<Customers />} allowedRoles={['Sales']} />} />
          <Route path="/staff/admin-users" element={<ProtectedRoute element={<AdminUsers />} allowedRoles={['SuperAdmin']} />} />
          <Route path="/staff/admin-dashboard" element={<ProtectedRoute element={<AdminDashboard />} allowedRoles={['Admin', 'SuperAdmin']} />} />
          <Route path="/staff/admin/map-iron-bluff" element={<ProtectedRoute element={<IronBluffMapper />} allowedRoles={['Admin', 'SuperAdmin']} />} />
          <Route path="/staff/operations-dashboard" element={<ProtectedRoute element={<OperationsDashboard />} allowedRoles={['Operations']} />} />
          <Route path="/staff/project-manager-dashboard" element={<ProtectedRoute element={<ProjectManagerDashboard />} allowedRoles={['ProjectManager']} />} />
          <Route path="/staff/superintendent-dashboard" element={<ProtectedRoute element={<SuperintendentDashboard />} allowedRoles={['Superintendent']} />} />
          
          {/* Vendor Portal Routes */}
          <Route path="/vendors/dashboard" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorDashboard />
                </VendorProvider>
              } 
              allowedRoles={['Vendor']} 
            />
          } />
          {/* Redirect old grid route to enhanced grid */}
          <Route path="/vendors/bids/grid" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          
          {/* Legacy grid still accessible at explicit path */}
          <Route path="/vendors/bids/grid-legacy" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidGridEnhanced />
                </VendorProvider>
              } 
              allowedRoles={['Vendor']} 
            />
          } />
          <Route path="/vendors/bids/grid-enhanced" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidGridEnhanced />
                </VendorProvider>
              } 
              allowedRoles={['Vendor']} 
            />
          } />
          <Route path="/vendors/bids/comparison" element={
            <ProtectedRoute 
              element={
                <VendorProvider>
                  <VendorBidComparison />
                </VendorProvider>
              } 
              allowedRoles={['Vendor']} 
            />
          } />
          <Route path="/vendors/bids/submit" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          
          {/* Legacy Routes - Redirect to new structure */}
          <Route path="/sales-dashboard" element={<Navigate to="/staff/sales-dashboard" replace />} />
          <Route path="/admin-dashboard" element={<Navigate to="/staff/admin-dashboard" replace />} />
          <Route path="/admin-users" element={<Navigate to="/staff/admin-users" replace />} />
          <Route path="/operations-dashboard" element={<Navigate to="/staff/operations-dashboard" replace />} />
          <Route path="/project-manager-dashboard" element={<Navigate to="/staff/project-manager-dashboard" replace />} />
          <Route path="/superintendent-dashboard" element={<Navigate to="/staff/superintendent-dashboard" replace />} />
          <Route path="/vendor-dashboard" element={<Navigate to="/vendors/dashboard" replace />} />
          <Route path="/staff/login" element={<Navigate to="/business/login" replace />} />
          <Route path="/vendors/login" element={<Login setRole={setRole} />} />
          <Route path="/vendors" element={<Navigate to="/vendors/login" replace />} />
          
          {/* Catch all possible vendor bid routes and redirect to integration */}
          <Route path="/vendor/bids" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bids/*" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bid" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/bid/*" element={<Navigate to="/vendors/bids/grid-enhanced" replace />} />
          <Route path="/vendor/*" element={<Navigate to="/vendors/*" replace />} />
          <Route path="/house-selection" element={<Navigate to="/customer/selection" replace />} />
          <Route path="/mortgage-details" element={<Navigate to="/customer/mortgage" replace />} />
          <Route path="/location/*" element={<Navigate to={`/customer/location/${location.pathname.slice(10)}`} replace />} />
          <Route path="/selection-summary" element={<Navigate to="/customer/selection-summary" replace />} />
          <Route path="/selection" element={<Navigate to="/customer/selection" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AnimatedContainer>
    </>
  );
};

const App = () => (
  <CustomerProvider>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PriceProvider>
        <style>{textureAnimations}</style>
        <AppContent />
      </PriceProvider>
    </BrowserRouter>
  </CustomerProvider>
);

export default App;